import React, { useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Shield,
  AlertTriangle,
  Check,
  Loader2,
  Clock,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Turnstile from '../components/Turnstile';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmergencyAccessRequest = () => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || '';
  
  const [code, setCode] = useState(codeFromUrl);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!code.trim() || !name.trim() || !email.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security verification');
      return;
    }
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch(`${API_URL}/api/emergency-access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.toUpperCase().trim(),
          requesterName: name.trim(),
          requesterEmail: email.trim(),
          reason: reason.trim(),
          turnstileToken,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        setExpiresAt(data.expires_at);
      } else {
        setError(data.error || 'Failed to submit request');
      }
    } catch (err) {
      setError('Unable to connect to the server. Please check your internet connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <Link to="/" className="text-xl font-heading font-semibold text-foreground">
            Lifely
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {!submitted ? (
            /* Request Form */
            <Card className="border border-border">
              <CardContent className="p-6 space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-amber-600" />
                  </div>
                  <h1 className="text-xl font-heading font-semibold">Emergency Access Request</h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    Request access to someone's Lifely information in case of emergency.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Emergency Access Code *</Label>
                    <Input
                      id="code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="XXXXXX"
                      className="text-center font-mono text-lg tracking-widest uppercase"
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Your Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      We'll contact you when access is granted.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason for Request (Optional)</Label>
                    <Textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Briefly explain why you need access..."
                      className="resize-none"
                      rows={3}
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Submit Request
                  </Button>
                </form>

                <div className="pt-4 border-t border-border">
                  <Turnstile onVerify={handleTurnstileVerify} onExpire={handleTurnstileExpire} />
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center">
                    The account holder will be notified of your request and has 48 hours to respond.
                    If they don't respond, access will be granted automatically.
                    This code is single-use and will be invalidated after this request.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Success State */
            <Card className="border border-border">
              <CardContent className="p-6 text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/50 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                
                <div>
                  <h1 className="text-xl font-heading font-semibold">Request Submitted</h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    The account holder has been notified of your request.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Access will be granted by:</span>
                  </div>
                  <p className="font-medium mt-1">{formatDate(expiresAt)}</p>
                </div>

                <div className="text-sm text-muted-foreground space-y-2">
                  <p><strong>What happens next:</strong></p>
                  <ul className="text-left space-y-1">
                    <li>• The account holder receives an email notification</li>
                    <li>• They can deny the request if it's not legitimate</li>
                    <li>• If no response in 48 hours, their continuity process triggers</li>
                    <li>• You'll be contacted at <strong>{email}</strong> when access is available</li>
                  </ul>
                </div>

                <Button variant="outline" asChild className="w-full">
                  <Link to="/">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Return to Lifely
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-4">
        <div className="max-w-2xl mx-auto px-4 text-center text-xs text-muted-foreground">
          <p>Lifely, your calm space for mapping what matters.</p>
        </div>
      </footer>
    </div>
  );
};

export default EmergencyAccessRequest;
